import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, ChevronDown, ChevronRight, AlertCircle, Info, Layers, MessageCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { useSidebar } from '../../contexts/SidebarContext';
import { parseGaebXml, flattenItems, serializeForChat } from '../../services/gaebParser';
import ChatView, { GEMINI_CHAT_MODELS } from '../chat/ChatView';

/* ──────────────── File Upload Area ──────────────── */
const FileUploadArea = ({ onFileLoaded, isLoading }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => onFileLoaded(e.target.result, file.name);
    reader.readAsText(file, 'UTF-8');
  }, [onFileLoaded]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.X83') || file.name.endsWith('.x83') || file.name.endsWith('.xml'))) {
      handleFile(file);
    }
  }, [handleFile]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
        dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
      }`}
      onClick={() => document.getElementById('gaeb-file-input').click()}
    >
      <input
        id="gaeb-file-input"
        type="file"
        accept=".X83,.x83,.xml"
        className="hidden"
        onChange={(e) => handleFile(e.target.files[0])}
      />
      <Upload className={`h-12 w-12 mx-auto mb-4 ${dragOver ? 'text-green-500' : 'text-gray-400'}`} />
      <p className="text-lg font-medium text-gray-700">
        {isLoading ? 'Datei wird verarbeitet...' : 'GAEB X83 Datei hochladen'}
      </p>
      <p className="text-sm text-gray-500 mt-2">
        Drag & Drop oder klicken zum Auswählen (.X83 / .xml)
      </p>
    </div>
  );
};

/* ──────────────── Project Header ──────────────── */
const ProjectHeader = ({ data }) => {
  const { project, owner, boqInfo, meta, award } = data;
  const catLabel = award.category === 'OpenProc' ? 'Öffentliche Ausschreibung' : award.category;
  const ownerLine = [owner.name1, owner.name2, owner.name3, `${owner.street}, ${owner.pcode} ${owner.city}`].filter(Boolean).join(' · ');
  return (
    <Card className="px-4 py-2.5 mb-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="text-base font-semibold text-gray-900 truncate">{project.label}</h2>
            <span className="text-xs text-gray-400 flex-shrink-0">{boqInfo.date || meta.date} · {project.currency}</span>
          </div>
          <p className="text-xs text-gray-500 truncate">Nr: {project.name} · {boqInfo.label} · {catLabel}</p>
        </div>
        <p className="text-xs text-gray-400 text-left flex-shrink-0">{ownerLine}</p>
      </div>
    </Card>
  );
};

/* ──────────────── BoQ Tree Node ──────────────── */
const BoQItemRow = ({ item, depth = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  if (item.type === 'remark') {
    return (
      <div className={`py-2 px-4 bg-amber-50 border-l-4 border-amber-400 my-1 rounded-r`} style={{ marginLeft: depth * 16 }}>
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">{item.shortText || 'Hinweis'}</p>
            {item.detailText && <p className="text-xs text-amber-700 mt-1 whitespace-pre-line">{item.detailText}</p>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginLeft: depth * 16 }} className="border-b border-green-100/50">
      <div className="flex items-center py-2 px-4 hover:bg-green-50/40 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <button className="mr-2 text-green-400">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className="text-sm font-mono text-green-700 w-20 flex-shrink-0">{item.rnoPart}</span>
        <span className="text-sm text-slate-800 flex-1 truncate">{item.shortText || '—'}</span>
        <span className="text-sm text-slate-700 w-24 text-right">{item.qty.toLocaleString('de-DE', { maximumFractionDigits: 3 })}</span>
        <span className="text-sm text-slate-500 w-16 text-center">{item.qu}</span>
        {item.lumpSum && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-2">Psch</span>}
      </div>
      {expanded && (
        <div className="px-4 pb-3 ml-8 bg-green-50/30 rounded-b">
          <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">{item.detailText}</p>
          {item.textComplements.length > 0 && (
            <div className="mt-2 space-y-1">
              {item.textComplements.map((tc, i) => (
                <div key={i} className="text-xs bg-white border border-green-100 rounded p-2">
                  <span className="font-medium text-slate-600">{tc.caption}</span>
                  <span className="text-green-700">{tc.body}</span>
                </div>
              ))}
            </div>
          )}
          {item.stlNo && <p className="text-xs text-slate-400 mt-1">StLB: {item.stlNo} | Katalog: {item.ctlgCode}</p>}
        </div>
      )}
    </div>
  );
};

const BoQCategoryNode = ({ category, depth = 0 }) => {
  const [expanded, setExpanded] = useState(depth < 1);
  const itemCount = flattenItems([category]).filter(i => i.type === 'item').length;
  return (
    <div>
      <div
        className={`flex items-center py-3 px-4 cursor-pointer hover:bg-green-50/40 transition-colors ${depth === 0 ? 'bg-green-50/50 font-semibold' : 'bg-white'}`}
        style={{ paddingLeft: 16 + depth * 16 }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-4 w-4 mr-2 text-green-600" /> : <ChevronRight className="h-4 w-4 mr-2 text-green-400" />}
        <span className="font-mono text-green-700 mr-3 text-sm">{category.rnoPart}</span>
        <span className="text-slate-800 text-sm flex-1">{category.label}</span>
        <span className="text-xs text-slate-400">{itemCount} Pos.</span>
      </div>
      {expanded && (
        <div>
          {category.items.map((item, i) => <BoQItemRow key={item.id || i} item={item} depth={depth + 1} />)}
          {category.children.map((child) => <BoQCategoryNode key={child.fullPath} category={child} depth={depth + 1} />)}
        </div>
      )}
    </div>
  );
};

/* ──────────────── Gaeb Chat (wraps shared ChatView) ──────────────── */
const PDF_PROCESSOR_URL = process.env.REACT_APP_PDF_PROCESSOR_URL || 'http://localhost:8000';

const GaebChat = ({ parsedData }) => {
  const [sessionId, setSessionId] = useState(null);
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    if (!parsedData) return;
    let cancelled = false;
    const createSession = async () => {
      try {
        const context = serializeForChat(parsedData);
        const res = await fetch(`${PDF_PROCESSOR_URL}/api/gaeb/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context }),
        });
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSessionId(data.session_id);
      } catch (err) {
        if (!cancelled) setSessionError(err.message);
      }
    };
    createSession();
    return () => { cancelled = true; };
  }, [parsedData]);

  return (
    <ChatView
      sessionId={sessionId}
      sessionError={sessionError}
      chatUrl={`${PDF_PROCESSOR_URL}/api/gaeb/chat`}
      emptyHint="Fragen zum Leistungsverzeichnis?"
      inputPlaceholder="Frage zum Leistungsverzeichnis stellen..."
      availableModels={GEMINI_CHAT_MODELS}
      modelStorageKey="gaebChatModelId"
    />
  );
};

/* ──────────────── Main Component ──────────────── */
export const Leistungsverzeichnisse = () => {
  const { isExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, setFileName] = useState('');
  const [activeTab, setActiveTab] = useState('tree');

  const handleFileLoaded = useCallback((content, name) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = parseGaebXml(content);
      setParsedData(data);
      setFileName(name);
      setActiveTab('tree');
    } catch (err) {
      setError(err.message);
      setParsedData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const tabs = [
    { id: 'tree', label: 'Leistungsverzeichnis', icon: Layers },

    { id: 'chat', label: 'Eveline', icon: MessageCircle },
  ];


  return (
    <div className={`${leftMargin} h-screen bg-gray-50 transition-all duration-300 flex flex-col overflow-hidden`}>
      <div className="p-8 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <FileText className="h-7 w-7 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leistungsverzeichnisse</h1>
            <p className="text-sm text-gray-500">GAEB DA XML (X83) Dateien hochladen und analysieren</p>
          </div>
        </div>

        {!parsedData && !error && <FileUploadArea onFileLoaded={handleFileLoaded} isLoading={isLoading} />}

        {error && (
          <Card className="p-6 border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <div>
                <p className="font-medium text-red-800">Fehler beim Verarbeiten der Datei</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
            <button onClick={() => { setError(null); setParsedData(null); }} className="mt-4 text-sm text-red-600 underline">
              Neue Datei hochladen
            </button>
          </Card>
        )}

        {parsedData && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-shrink-0">
              <ProjectHeader data={parsedData} />
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1 bg-white rounded-lg border border-gray-200 p-1">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeTab === tab.id ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setParsedData(null); setFileName(''); setError(null); }}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  Neue Datei
                </button>
              </div>
            </div>
            <Card className="overflow-hidden flex-1 min-h-0 flex flex-col">
              {activeTab === 'tree' && (
                <div className="divide-y divide-green-100/50 overflow-y-auto flex-1">
                  {parsedData.categories.map(cat => (
                    <BoQCategoryNode key={cat.fullPath} category={cat} depth={0} />
                  ))}
                </div>
              )}
              {activeTab === 'chat' && <GaebChat parsedData={parsedData} />}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leistungsverzeichnisse;
